import styled, { css } from "styled-components";
import AntInput from "antd/es/input";
import { hexToRGB } from "@shared/utils/misc";
import {
  getNewLookPlaceholderColor,
  inputBox,
  newLookFieldStates,
} from "common/Inputs/styles";
import type { ComponentType } from "react";

export interface SharedInputProps {
  $flushPaddingLeft?: boolean;
  $bgColor?: string;
}

export const inputStyles = css<SharedInputProps>`
  &&& {
    ${(props) =>
      props.$flushPaddingLeft &&
      `
    touch-action: manipulation;
    &.ant-input, &.ant-input-affix-wrapper {
      padding-left: 0 !important;
    }
  `}
    ${inputBox};

    &.ant-input-affix-wrapper {
      border-color: ${(p) => p.theme.colors.lightBorderGrey};
      background: ${(p) => p.$bgColor || p.theme.colors.cardBgColor};
    }

    &.ant-input-search > .ant-input-wrapper > .ant-input-affix-wrapper {
      background: ${(p) => p.theme.colors.cardBgColor};
    }

    &.ant-input {
      background: ${(p) => p.$bgColor || p.theme.colors.cardBgColor} !important;
      color: ${(p) => p.theme.colors.primaryGrey};
    }

    &.ant-input-affix-wrapper-focused:not(.ant-input-borderless) {
      box-shadow: 0 0 0 2px ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.3)};
    }

    .ant-input-clear-icon,
    .ant-input-clear-icon:hover {
      color: ${(p) => p.theme.colors.secondaryGrey};
    }

    .ant-input-number-handler-wrap {
      background: ${(p) => p.$bgColor || p.theme.colors.cardBgColor};
    }

    &.ant-input-affix-wrapper:not(.ant-input-borderless):hover,
    &.ant-input-affix-wrapper-focused:not(.ant-input-borderless),
    &:not(.ant-input-borderless):hover,
    &:not(.ant-input-borderless):focus {
      border: 1px solid ${(p) => p.theme.colors.primaryBlue};
    }

    &.ant-input-affix-wrapper > .ant-input {
      border: none;
    }

    &.ant-input::placeholder,
    .ant-input::placeholder {
      color: ${(p) => p.theme.colors.lightestGrey};
    }

    &:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 30px
        ${(p) => p.$bgColor || p.theme.colors.cardBgColor} inset !important;
      -webkit-text-fill-color: ${(p) => p.theme.colors.textColor} !important;
    }

    &.ant-input:not(.ant-input-borderless):focus {
      box-shadow: 0 0 0 2px ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.2)};
    }

    &.ant-input-affix-wrapper-borderless {
      border: unset !important;
      box-shadow: unset !important;
    }

    &.ant-input-borderless {
      background: transparent;
      border: none;
    }
    .ant-input[disabled] {
      color: ${(p) => p.theme.colors.disabledText};
    }

    ${(p) =>
      p.theme.isNewLook &&
      css`
        &.ant-input:not(.ant-input-borderless),
        &.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-borderless) {
          ${newLookFieldStates};
        }
        &.ant-input-affix-wrapper-status-error:not(
            .ant-input-affix-wrapper-borderless
          ),
        &.ant-input-status-error:not(.ant-input-borderless) {
          border-color: ${p.theme.colors.red};
        }
        .ant-input-prefix {
          margin-inline-end: 8px;
          color: ${p.theme.colors.secondaryText};
        }
        .ant-input-suffix {
          color: ${p.theme.colors.secondaryText};
        }
        &.ant-input-disabled,
        &.ant-input-affix-wrapper-disabled {
          background: ${p.theme.colors.cardLightBgColor} !important;
        }
        .ant-input-clear-icon,
        .ant-input-password-icon {
          color: ${p.theme.colors.secondaryText};
        }
        &.ant-input::placeholder,
        .ant-input::placeholder {
          color: ${getNewLookPlaceholderColor(p.theme)};
        }

        /* Addon groups and the search box are wrappers: the field inside
           carries the border, so the wrapper must not draw a second one. */
        &&.ant-input-group-wrapper,
        &&.ant-input-search.ant-space-compact {
          border: none;
          background: transparent;
          box-shadow: none;
        }
        &.ant-input-group-wrapper .ant-input {
          ${newLookFieldStates};
        }
        &.ant-input-search .ant-input-search-btn {
          border-color: ${p.theme.colors.lightBorderGrey};
          background: ${p.$bgColor || p.theme.colors.cardBgColor};
          color: ${p.theme.colors.secondaryText};
          box-shadow: none;
          &:hover {
            border-color: ${p.theme.colors.mediumBorderGrey};
            color: ${p.theme.colors.primaryBlue};
          }
        }
        &.ant-input-search .ant-input:focus + .ant-input-search-btn {
          border-inline-start-color: ${p.theme.colors.primaryBlue};
        }
      `}
  }
`;

const Input = styled(AntInput)<SharedInputProps>`
  ${inputStyles}
`;
Input.Password = styled(AntInput.Password)<SharedInputProps>`
  ${inputStyles};
  .ant-input-password-icon {
    color: ${(p) => p.theme.colors.secondaryGrey} !important;
  }
`;
Input.Search = styled(AntInput.Search)<SharedInputProps>`
  ${inputStyles};
  &.ant-input-group-wrapper.ant-input-search.ant-input-search-with-button:not(
      .ant-input-borderless
    ):hover {
    border: none !important;
  }
  &.ant-input-group-wrapper .ant-input-group .ant-input {
    border-radius: 6px 0 0 6px !important;
  }
  &.ant-input-search
    .ant-input-group
    .ant-input-group-addon:last-child
    .ant-input-search-button {
    border-radius: 0 6px 6px 0 !important;
  }
`;

export const StylableInput = Input as ComponentType<Record<string, unknown>>;
export const StylablePasswordInput = Input.Password as ComponentType<
  Record<string, unknown>
>;

export default Input;
